import type { ChecklistApp } from "src/api/fraction";
import { type DisplayApp, toDisplayApp } from "src/utilities/displayApp";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface SearchableApp extends DisplayApp {
  searchText: string;
}

export const isSearchablePipelineApp = (app: ChecklistApp | null | undefined): app is ChecklistApp =>
  !!app?.id;

function toSearchableApp(app: ChecklistApp): SearchableApp {
  const display = toDisplayApp(app);
  return {
    ...display,
    searchText:
      `${display.applicantNames} ${display.brokerNames} ${display.eid || ""} ${display.address}`.toLowerCase(),
  };
}

interface SearchableAppsStore {
  /** Map serialized as entries array for persistence */
  entries: [string, SearchableApp][];

  /** Bulk-update from pipeline data. */
  updateFromPipeline: (apps: (ChecklistApp | null | undefined)[]) => void;

  /** Update a single app (e.g. after a mutation or individual fetch). */
  updateApp: (app: ChecklistApp) => void;
}

// Runtime lookup map rebuilt from persisted entries
let lookupMap = new Map<string, SearchableApp>();
let cachedArray: SearchableApp[] = [];
let arrayVersion = 0;
let currentVersion = 0;

function rebuildLookup(entries: [string, SearchableApp][]) {
  lookupMap = new Map(entries);
  currentVersion++;
}

/** Get all searchable apps as a stable array (call outside React or in callbacks). */
export function getSearchableApps(): SearchableApp[] {
  if (arrayVersion !== currentVersion) {
    cachedArray = Array.from(lookupMap.values());
    arrayVersion = currentVersion;
  }
  return cachedArray;
}

export const useSearchableAppsStore = create<SearchableAppsStore>()(
  persist(
    (set, get) => ({
      entries: [],

      updateFromPipeline: (pipelineApps) => {
        let changed = false;
        for (const app of pipelineApps) {
          if (!isSearchablePipelineApp(app)) {
            continue;
          }
          if (!lookupMap.has(app.id)) {
            lookupMap.set(app.id, toSearchableApp(app));
            changed = true;
          }
        }
        if (changed) {
          currentVersion++;
          set({ entries: Array.from(lookupMap.entries()) });
        }
      },

      updateApp: (app) => {
        lookupMap.set(app.id, toSearchableApp(app));
        currentVersion++;
        set({ entries: Array.from(lookupMap.entries()) });
      },
    }),
    {
      name: "searchable-apps-index",
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state?.entries) {
          rebuildLookup(state.entries);
        }
      },
    }
  )
);
